/* eslint-disable consistent-return */
/* eslint-disable react/no-danger */
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useResponsive } from 'src/@context/Responsive';
import useStyles from './DetailPage.styles';
import Diaporama from './Diaporama';
import Image from './Image';
import VideoEmbed from './VideoEmbed';

type TDetailPage = {
  title?: string;
  images: PictureRef[];
  slides: IDto[];
  text?: string;
  mainText?: string;
  portalUID?: string;
  place?: string;
  year?: string;
  pdf?: FileRef;
  orientation?: OverviewOrientation;
  name?: string;
  role?: string;
  email?: string;
  previousUrl?: string;
  previousTitle?: string;
  nextUrl?: string;
  nextTitle?: string;
  // Identifies the entry being shown: its UID from the *fetched data*, not the
  // URL. The URL changes before the data arrives, and keying on it remounted
  // the diaporama against the previous entry's image count. Not portalUID
  // either: that is the parent overview's UID, shared by every sibling.
  entryKey?: string;
};

const DetailPage: FC<TDetailPage> = ({
  entryKey,
  images,
  slides,
  text,
  mainText,
  portalUID,
  title,
  place,
  year,
  pdf,
  orientation,
  name,
  role,
  email,
  previousUrl,
  previousTitle,
  nextUrl,
  nextTitle,
}) => {
  const cls = useStyles();
  const { steps } = useResponsive();

  const sizes = `(max-width: 768px) calc(100vw - 20px),
	(max-width: 1024px) calc(100vw - 300px),
	(max-width: 1280px) calc(100vw - 350px),
	(max-width: 1920px) calc(100vw - 400px),
	(max-width: 3000px) 2100px`;

  const onClickUp = useCallback(() => {
    window.scroll({ top: 0, behavior: 'smooth' });
  }, []);

  const mainTextRef = useRef<HTMLDivElement | null>(null);

  const onClickDown = useCallback(() => {
    const mainMenuEl = document.getElementById('main-menu');
    const mainTextEl = mainTextRef.current;
    if (!mainMenuEl || !mainTextEl) {
      window.scroll({ top: window.innerHeight, behavior: 'smooth' });
    } else {
      window.scroll({
        top:
          mainTextEl.getBoundingClientRect().top -
          mainMenuEl.getBoundingClientRect().top,
        behavior: 'smooth',
      });
    }
  }, []);

  // Team entries carry their caption as data. Anything else (projects,
  // collection items) passes none and keeps its existing layout.
  // The diaporama already owns the side click zones when there is more than
  // one slide to step through, so entry navigation stands down in that case.
  const entryNav = (previousUrl || nextUrl) && images.length + slides.length <= 1 && (
    <>
      {/* aria-label only: a title attribute would pop a browser tooltip. */}
      {previousUrl && (
        <Link
          to={previousUrl}
          className={classNames(cls.entryBtn, cls.entryPrevious)}
          aria-label={previousTitle ? `Previous: ${previousTitle}` : 'Previous'}
        />
      )}
      {nextUrl && (
        <Link
          to={nextUrl}
          className={classNames(cls.entryBtn, cls.entryNext)}
          aria-label={nextTitle ? `Next: ${nextTitle}` : 'Next'}
        />
      )}
    </>
  );

  const caption = (role || email) && (
    <p className={cls.caption}>
      {name && <span className={cls.captionName}>{name}</span>}
      {role && <span className={cls.captionRole}>{role}</span>}
      {email && (
        <a href={`mailto:${email}`} className={cls.captionEmail}>
          {email}
        </a>
      )}
    </p>
  );

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [pageIsScrollable, setPageIsScrollable] = useState(false);
  useEffect(() => {
    const onResize = () => {
      setPageIsScrollable(
        document.documentElement.scrollHeight >
          document.documentElement.clientHeight
      );
    };
    onResize();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const resizeObserver = new ResizeObserver(onResize);
    window.addEventListener('resize', onResize);
    if (rootRef.current) {
      resizeObserver.observe(rootRef.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      resizeObserver.disconnect();
    };
  }, []);
  const [downButtonHidden, setDownButtonHidden] = useState(pageIsScrollable);
  useEffect(() => {
    const onScroll = () => {
      setDownButtonHidden(
        !pageIsScrollable || (document?.scrollingElement?.scrollTop || 0) > 10
      );
    };
    onScroll();
    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [pageIsScrollable]);

  const [containerWidth, setContainerWidth] = useState<string | number>('100%');
  const [
    diaporamaContainerEl,
    setDiaporamaContainerEl,
  ] = useState<HTMLDivElement | null>(null);
  const calculateContainerSize = useCallback(() => {
    if (!diaporamaContainerEl) {
      return;
    }
    const header = document.getElementById('header');
    const width = Math.floor(diaporamaContainerEl.offsetWidth);
    // Hack: Due to CSS layout weirdness (or my incompetence), the
    // images initially don't take up the correct amount of space.
    // So if the container is smaller than 1/4 of the window, we assume
    // it's the first render.
    if (width < 0.25 * window.innerWidth) {
      return;
    }
    // Narrowing the header to the image keeps the language switcher on the
    // photo's right edge. On a landscape page that is nearly the column edge
    // anyway; on a portrait page it would pull the switcher well inward, so
    // it would jump between pages. Portrait keeps the header at full width.
    if (header) {
      header.style.maxWidth = orientation === 'portrait' ? '' : `${width}px`;
    }
    setContainerWidth(width);
  }, [diaporamaContainerEl, orientation]);
  useLayoutEffect(() => {
    if (steps.includes('AfterTablet')) {
      calculateContainerSize();
    }
  }, [calculateContainerSize, steps]);
  useEffect(() => {
    if (
      !diaporamaContainerEl ||
      images.length + slides.length === 0 ||
      !steps.includes('AfterTablet')
    ) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const observer = new ResizeObserver(calculateContainerSize);
    observer.observe(diaporamaContainerEl);

    return () => {
      observer.disconnect();

      const header = document.getElementById('header');
      if (header) {
        header.style.maxWidth = '';
      }
    };
  }, [
    calculateContainerSize,
    diaporamaContainerEl,
    images.length,
    slides.length,
    steps,
  ]);

  return (
    <article className={cls.root} ref={rootRef} data-orientation={orientation}>
      {/* Tablet Layout */}
      {steps.includes('Mobile') && (
        <div className={cls.mobileWrapper}>
          {images.length > 0 &&
            images.map((img) => (
              <figure className={cls.figure} key={img.Id}>
                <Image alt={img.Legend} pictureRef={img} sizes={sizes} />
              </figure>
            ))}
          {slides.map((slide, i) => {
            if (slide.type === 'DiaporamaItemImage') {
              const img = (slide as DiaporamaItemImageDto).Image;

              return (
                <figure className={cls.figure} key={img.Id}>
                  <Image alt={img.Legend} pictureRef={img} sizes={sizes} />
                </figure>
              );
            }

            if (slide.type === 'DiaporamaItemVideo') {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <div className={cls.figure} key={i}>
                  <VideoEmbed slide={slide as DiaporamaItemVideoDto} />
                </div>
              );
            }

            return null;
          })}
          {caption}
          {entryNav}
          <div className={cls.content}>
            <h2 className={cls.title}>{title}</h2>
            {(place || year) && (
              <h3 className={cls.title}>{`${place}, ${year}`}</h3>
            )}
            {text && (
              <div
                dangerouslySetInnerHTML={{ __html: text }}
                className={cls.text}
              />
            )}
            {pdf && (
              <a
                href={pdf.Src}
                className={cls.pdfLink}
                target='_blank'
                rel='noopener noreferrer'
              >
                PDF MAGAZIN
              </a>
            )}
          </div>
        </div>
      )}
      {/* Tablet Layout */}
      {steps.includes('Tablet') && (
        <>
          <div className={cls.mobileWrapper}>
            {images.length > 0 &&
              images.map((img) => (
                <figure className={cls.figure} key={img.Id}>
                  <Image alt={img.Legend} pictureRef={img} sizes={sizes} />
                </figure>
              ))}
            {slides.map((slide, i) => {
              if (slide.type === 'DiaporamaItemImage') {
                const img = (slide as DiaporamaItemImageDto).Image;

                return (
                  <figure className={cls.figure} key={img.Id}>
                    <Image alt={img.Legend} pictureRef={img} sizes={sizes} />
                  </figure>
                );
              }

              if (slide.type === 'DiaporamaItemVideo') {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={cls.figure} key={i}>
                    <VideoEmbed slide={slide as DiaporamaItemVideoDto} />
                  </div>
                );
              }

              return null;
            })}
            {caption}
            {entryNav}
          </div>
        </>
      )}

      {/* AfterTablet Layout */}
      {steps.includes('AfterTablet') && images.length + slides.length > 0 && (
        <>
          <div
            className={cls.diaporamaContainerDesktop}
            ref={setDiaporamaContainerEl}
          >
            {/* Keyed per entry so the slide index resets when the arrows carry
                over into the next person; without it a stale index from a
                two-image entry would point past a one-image entry's slides. */}
            <Diaporama
              key={entryKey}
              images={images}
              slides={slides}
              previousUrl={previousUrl}
              nextUrl={nextUrl}
            />
            {caption}
          </div>
        </>
      )}
      {mainText && (
        <div
          className={cls.mainTextContainer}
          ref={mainTextRef}
          style={diaporamaContainerEl ? { maxWidth: containerWidth } : {}}
        >
          <div
            className={cls.downButtonContainer}
            data-hidden={downButtonHidden}
          >
            <button
              type='button'
              className={cls.downButton}
              title='Scroll down to text'
              onClick={onClickDown}
            >
              Kontext
              <img
                src='/images/arrow-down.svg'
                width='40'
                height='15'
                alt='Arrow pointing downwards'
                aria-hidden
              />
            </button>
          </div>
          <div
            className={cls.mainText}
            dangerouslySetInnerHTML={{ __html: mainText }}
            data-showText={downButtonHidden}
          />
          {pageIsScrollable && (
            <div className={cls.upButtonContainer}>
              <button
                type='button'
                className={cls.upButton}
                title='Go back up'
                onClick={onClickUp}
              >
                <img
                  src='/images/arrow-up.svg'
                  width='40'
                  height='15'
                  alt='Arrow pointing upwards'
                  aria-hidden
                />
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

export default DetailPage;
